'use client';

import { useState, useEffect } from 'react';
import ProcessStep from './ProcessStep';
import InteractiveDemo from './InteractiveDemo';
import FAQSection from './FAQSection';

interface ProcessStepData {
  number: string;
  title: string;
  description: string;
  icon: string;
  details: string[];
}

const HowItWorksInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const processSteps: ProcessStepData[] = [
    {
      number: "Step 01",
      title: "Order Your ParkPing Card",
      description: "Order your ParkPing card online. Each card is uniquely linked to your account and designed for long-term use.",
      icon: "CreditCardIcon",
      details: [
        "Unique QR code and ParkPing contact number",
        "Durable card suitable for daily use",
        "Designed to remain visible inside the vehicle",
        "Clean and professional design"
      ]
    },
    {
      number: "Step 02",
      title: "Activate Your Card",
      description: "Scan the QR code on the card and complete a simple verification step to link it with your phone number.",
      icon: "DevicePhoneMobileIcon",
      details: [
        "One-time activation process",
        "Phone number linked securely",
        "No app installation required",
        "Support available if assistance is needed"
      ]
    },
    {
      number: "Step 03",
      title: "Place the Card Inside Your Vehicle",
      description: "Keep the ParkPing card on your dashboard where it is easily visible from outside the vehicle.",
      icon: "EyeIcon",
      details: [
        "Easy to place and reposition",
        "Clearly visible for anyone nearby",
        "Does not interfere with driving",
        "Minimal and professional look"
      ]
    },
    {
      number: "Step 04",
      title: "Receive Calls Securely",
      description: "When someone scans the QR code or calls the ParkPing number, ParkPing routes the call to you without revealing your personal phone number.",
      icon: "PhoneIcon",
      details: [
        "Calls routed through ParkPing",
        "Your phone number remains private",
        "Caller does not see personal details",
        "No app required for callers"
      ]
    }
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-gray-200 rounded-lg w-3/4 mx-auto"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto"></div>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-48 bg-gray-100 rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-4 px-6 py-2 bg-[#42AF47]/10 rounded-full border border-[#42AF47]/20">
                <span className="text-sm font-semibold text-[#42AF47]">Simple 4-Step Process</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
                How ParkPing Works
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                A clear and straightforward process that allows others to reach you during parking situations, without sharing personal contact details.
              </p>
            </div>

            <div className="space-y-16 lg:space-y-24">
              {processSteps.map((step, index) => (
                <ProcessStep key={index} step={step} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <InteractiveDemo />
      <FAQSection />
    </>
  );
};

export default HowItWorksInteractive;